import React, { useContext, useEffect, useRef, useState } from 'react';
import { Drawer, Box, Button, useMediaQuery, Typography, Avatar, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import AssignmentIcon from '@mui/icons-material/Assignment';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SettingsIcon from '@mui/icons-material/Settings';
import GroupIcon from '@mui/icons-material/Group';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import PaidIcon from '@mui/icons-material/Paid';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ElectricalServicesIcon from '@mui/icons-material/ElectricalServices';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import ScienceIcon from '@mui/icons-material/Science';
import SidebarElement from './SidebarElement';
import { AuthContext } from './AuthContext';
import { SettingsContext } from './SettingsContext';
import Logo from '../assets/Elcare_Nordic_white.png';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import Cookies from 'js-cookie';

const menuItems = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    path: '/dashboard',
    icon: <HomeIcon />,
    roles: ['admin', 'superadmin'],
  },
  {
    key: 'activities',
    label: 'Activities',
    path: '/activities',
    icon: <AssignmentIcon />,
    roles: ['admin', 'superadmin'],
  },
  {
    key: 'stock',
    label: 'Stock',
    path: '/stock',
    icon: <InventoryIcon />,
    roles: ['admin', 'superadmin'],
  },
  {
    key: 'shipping',
    label: 'Shipping',
    path: '/shipping',
    icon: <LocalShippingIcon />,
    roles: ['admin', 'superadmin'],
  },
  {
    key: 'social',
    label: 'Social',
    path: '/social',
    icon: <GroupIcon />,
    roles: ['admin', 'superadmin'],
  },
  {
    key: 'workers',
    label: 'Workers',
    path: '/workers',
    icon: <BuildCircleIcon />,
    roles: ['admin', 'superadmin'],
  },
  {
    key: 'repair-cost',
    label: 'Repair Cost',
    path: '/repair-cost',
    icon: <PaidIcon />,
    roles: ['admin', 'superadmin'],
  },
  {
    key: 'price-admin',
    label: 'Price Admin',
    path: '/price-admin',
    icon: <MonetizationOnIcon />,
    roles: ['admin', 'superadmin'],
  },
  {
    key: 'esd-check',
    label: 'ESD Check',
    path: '/esd-check',
    icon: <FlashOnIcon />,
    roles: ['admin', 'superadmin'],
    requiredFunction: 'esdregister'
  },
  {
    key: 'esd-overview',
    label: 'ESD Overview',
    path: '/esd-overview',
    icon: <VerifiedUserIcon />,
    roles: ['admin', 'superadmin', 'user'],
    requiredFunction: 'esdoverview'
  },
  // Developer-only menu items (shown only if role === 'developer')
  {
    key: 'front',
    label: 'Frontend Docs',
    path: '/developer/front',
    icon: <ElectricalServicesIcon />,
    roles: ['developer'],
  },
  {
    key: 'back',
    label: 'Backend Docs',
    path: '/developer/back',
    icon: <BuildCircleIcon />,
    roles: ['developer'],
  },
  {
    key: 'feedbackreader',
    label: 'Feedback Reader',
    path: '/developer/feedbackreader',
    icon: <ElectricalServicesIcon />, // Velg ikon etter behov
    roles: ['superadmin', 'developer'],
  },
  {
    key: 'feedback',
    label: 'Feedback',
    path: '/developer/feedback',
    icon: <ElectricalServicesIcon />, // You can change icon if needed
    roles: ['superadmin', 'developer'],
  },
  {
    key: 'webork-server',
    label: 'WebOrk Server',
    path: '/webork-server',
    icon: <ScienceIcon />, // Passende ikon for WebOrk server
    roles: ['superadmin', 'developer', 'admin'], // Juster roller etter behov
  },
];

function Sidebar() {
  const { isAuthenticated, logout, role, functions: userFunctions } = useContext(AuthContext);
  const { useFloatingPanel, setIsSettingsOpen, isSidebarOpen, setIsSidebarOpen, autoHideSidebar, useSmallSidebar } = useContext(SettingsContext);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const mouseOutTimer = useRef(null);
  const [fetchedFunctions, setFetchedFunctions] = useState([]);

  const handleNavigate = (path) => {
    navigate(path);
  };

  const handleSettingsClick = () => {
    if (useFloatingPanel) {
      setIsSettingsOpen(true);
    } else {
      navigate('/settings');
    }
  };

  const handleLogin = () => navigate('/login');
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleMouseEnter = () => {
    if (mouseOutTimer.current) {
      clearTimeout(mouseOutTimer.current);
    }
  };

  const handleMouseLeave = () => {
    if (autoHideSidebar && isSidebarOpen) {
      mouseOutTimer.current = setTimeout(() => {
        setIsSidebarOpen(false);
      }, 1000);
    }
  };

  const iconSize = useSmallSidebar ? 20 : (isSmallScreen ? 30 : 40);

  // Hent functions fra cookies hvis tilgjengelig
  const cookieFunctions = (() => {
    try {
      const f = Cookies.get('functions');
      if (f) return JSON.parse(f);
    } catch (e) {}
    return [];
  })();

  // Bruk functions fra context hvis mulig, ellers fra cookie, ellers fetch
  const effectiveFunctions = Array.isArray(userFunctions) && userFunctions.length > 0
    ? userFunctions
    : (cookieFunctions.length > 0 ? cookieFunctions : fetchedFunctions);

  // Fetch functions fra nytt endepunkt hvis vi ikke har noen
  useEffect(() => {
    if (
      isAuthenticated &&
      (!Array.isArray(userFunctions) || userFunctions.length === 0) &&
      cookieFunctions.length === 0
    ) {
      const fetchFunctions = async () => {
        try {
          const token = Cookies.get('token');
          if (!token) return;
          const url = `${process.env.REACT_APP_STATUS_API_URL}/user/functions`;
          const response = await fetch(url, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            setFetchedFunctions(data.functions || []);
          }
        } catch (err) {
          // Optionally handle error
        }
      };
      fetchFunctions();
    }
  }, [isAuthenticated, userFunctions, cookieFunctions.length]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (autoHideSidebar && isSidebarOpen) {
        const drawer = document.querySelector('.MuiDrawer-paper');
        const toggleButton = document.querySelector('.sidebar-toggle-button');
        if (drawer && !drawer.contains(event.target) && 
            toggleButton && !toggleButton.contains(event.target)) {
          setIsSidebarOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [autoHideSidebar, isSidebarOpen, setIsSidebarOpen]);

  useEffect(() => {
    console.log("Role in Sidebar:", role);
    console.log("User functions in Sidebar:", userFunctions);
  }, [role, userFunctions]);

  const isEsdCheckPage = location.pathname === "/esd-check";
  const isDeveloperLandingPage = location.pathname === "/developer/landing";
  const isFeedbackPage = location.pathname === "/developer/feedback";
  const hideSidebar = isEsdCheckPage || isDeveloperLandingPage || isFeedbackPage;

  return (
    !hideSidebar && (
      <Drawer
        variant="permanent"
        open={autoHideSidebar ? isSidebarOpen : true}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          width: useSmallSidebar ? 120 : 180,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: useSmallSidebar ? 120 : 180,
            boxSizing: 'border-box',
            backgroundColor: '#252526',
            color: '#ffffff',
            overflowY: 'auto',
            transform: autoHideSidebar && !isSidebarOpen ? 'translateX(-100%)' : 'none',
            transition: 'transform 0.3s ease-in-out',
            '&::-webkit-scrollbar': {
              width: '4px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#252526',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(255, 255, 255, 0.15)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'rgba(255, 255, 255, 0.25)',
            },
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(255, 255, 255, 0.15) #252526',
          },
        }}
      >
        <Box sx={{ padding: '8px 16px 0 16px', textAlign: 'center' }}>
          <img
            src={Logo}
            alt="Verksted Logo"
            style={{ width: isSmallScreen ? '80%' : '100%', height: 'auto' }}
          />
        </Box>

        {!isSmallScreen && (
          <Box sx={{ padding: '4px 16px', textAlign: 'center', color: '#ffffff', marginTop: '-10px' }}>
            InStore
          </Box>
        )}

        {isAuthenticated && (
          <Box sx={{ paddingTop: '16px' }}>
            {/* If superadmin, show all. If developer or user has 'systemdocumentation', show developer docs. Ellers bruk effectiveFunctions. */}
            {role === 'superadmin' ? (
              menuItems.map(item => (
                <div key={item.key} onClick={() => handleNavigate(item.path)}>
                  <SidebarElement icon={item.icon} label={item.label} />
                </div>
              ))
            ) : (role === 'developer' || (Array.isArray(effectiveFunctions) && effectiveFunctions.includes('systemdocumentation'))) ? (
              menuItems.filter(item => ['front', 'back', 'feedbackreader', 'feedback'].includes(item.key)).map(item => (
                <div key={item.key} onClick={() => handleNavigate(item.path)}>
                  <SidebarElement icon={item.icon} label={item.label} />
                </div>
              ))
            ) : (
              // Bruk kun funksjonsnavn direkte som key for matching
              menuItems.filter(item => Array.isArray(effectiveFunctions) && effectiveFunctions.includes(item.key)).map(item => (
                <div key={item.key} onClick={() => handleNavigate(item.path)}>
                  <SidebarElement icon={item.icon} label={item.label} />
                </div>
              ))
            )}
          </Box>
        )}

        <Box sx={{ flexGrow: 1 }} />

        {role === 'superadmin' && (
          <Box sx={{ padding: 2, textAlign: 'center' }}>
            <div onClick={() => handleNavigate('/superadmin')}>
              <SidebarElement icon={<AdminPanelSettingsIcon sx={{ fontSize: iconSize, color: '#ff5722' }} />} label="Super Admin" />
            </div>
          </Box>
        )}

        {(isAuthenticated && (role === 'superadmin' || (Array.isArray(userFunctions) && userFunctions.includes('settings')))) && (
          <Box sx={{ padding: 2, textAlign: 'center' }}>
            <div onClick={handleSettingsClick}>
              <SidebarElement icon={<SettingsIcon sx={{ fontSize: iconSize, color: '#4caf50' }} />} label="Settings" />
            </div>
          </Box>
        )}

        <Box sx={{ padding: 2, textAlign: 'center' }}>
          {isAuthenticated ? (
            <Button 
              variant="contained" 
              onClick={handleLogout} 
              sx={{ 
                width: '100%', 
                backgroundColor: 'rgba(135,135,135,0.6)',
                '&:hover': { backgroundColor: 'rgba(135,135,135,0.8)' }
              }}
            >
              Logout
            </Button>
          ) : (
            location.pathname !== '/login' && (
              <Button variant="contained" color="primary" onClick={handleLogin} sx={{ width: '100%' }}>
                Login
              </Button>
            )
          )}
        </Box>
      </Drawer>
    )
  );
}

export default Sidebar;
